import { useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { MapPin, Calendar, Waves, Trash2, Eye, User, Image as ImageIcon } from "lucide-react";
import ImageLightbox from "@/components/ImageLightbox";
import { BACKEND_URL } from '@/utils/apiConfig';

const API = `${BACKEND_URL}/api`;

const DiveSiteReportsManagement = ({ reports, loading, token, onReportsUpdate }) => {
  const [expandedReportId, setExpandedReportId] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const handleDeleteReport = async (reportId) => {
    if (!confirm("Are you sure you want to delete this dive site report?")) return;
    
    try {
      await axios.delete(`${API}/admin/dive-site-reports/${reportId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Remove from local state
      onReportsUpdate(reports.filter(r => r.id !== reportId));
    } catch (err) {
      console.error("Failed to delete report:", err);
      alert("Failed to delete report");
    }
  };

  const getConditionBadge = (condition) => {
    const badges = {
      excellent: { bg: "bg-green-100", text: "text-green-700", label: "Excellent" },
      good: { bg: "bg-blue-100", text: "text-blue-700", label: "Good" },
      fair: { bg: "bg-yellow-100", text: "text-yellow-700", label: "Fair" },
      poor: { bg: "bg-orange-100", text: "text-orange-700", label: "Poor" },
      closed: { bg: "bg-red-100", text: "text-red-700", label: "Closed" }
    };
    
    const badge = badges[condition] || badges.good;
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    );
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="text-2xl">Dive Site Reports</CardTitle>
            <div className="text-sm text-gray-600">
              <span className="font-semibold">{reports.length}</span> Total Reports
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-center py-8 text-gray-600">Loading reports...</p>
          ) : reports.length === 0 ? (
            <div className="text-center py-12">
              <MapPin className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600">No dive site reports yet</p>
              <p className="text-sm text-gray-500 mt-2">Reports will appear here when users submit observations</p>
            </div>
          ) : (
            <div className="space-y-4">
              {reports.map((report) => (
                <div
                  key={report.id}
                  className="border-2 rounded-lg hover:border-cyan-200 transition-colors overflow-hidden"
                >
                  {/* Report Header */}
                  <div
                    className="p-4 bg-gray-50 cursor-pointer"
                    onClick={() => setExpandedReportId(expandedReportId === report.id ? null : report.id)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <MapPin className="w-5 h-5 text-cyan-600" />
                          <h3 className="text-lg font-bold text-gray-900">{report.dive_site_name}</h3>
                          {getConditionBadge(report.current_conditions)}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-600">
                          <div className="flex items-center gap-1">
                            <User className="w-4 h-4" />
                            <span>User ID: {report.user_id.slice(0, 8)}...</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            <span>{new Date(report.created_at).toLocaleDateString()}</span>
                          </div>
                          {report.image_urls && report.image_urls.length > 0 && (
                            <div className="flex items-center gap-1">
                              <ImageIcon className="w-4 h-4" />
                              <span>{report.image_urls.length} photos</span>
                            </div>
                          )}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteReport(report.id);
                        }}
                        className="bg-red-500 hover:bg-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {expandedReportId === report.id && (
                    <div className="p-6 bg-white border-t-2">
                      <div className="grid md:grid-cols-2 gap-6">
                        {/* Left Column - Conditions */}
                        <div className="space-y-4">
                          <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                            <Waves className="w-5 h-5 text-cyan-600" />
                            Dive Conditions
                          </h4>
                          
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-gray-600">Visibility:</span>
                              <span className="font-medium">{report.visibility_meters}m</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Water Temp:</span>
                              <span className="font-medium">{report.water_temp_celsius}°C</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Current:</span>
                              <span className="font-medium">{report.current_strength}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Entry/Exit:</span>
                              <span className="font-medium">{report.entry_exit_difficulty}</span>
                            </div>
                          </div>

                          {report.hazards_warnings && (
                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mt-4">
                              <p className="text-xs font-semibold text-yellow-800 mb-1">⚠️ Hazards/Warnings:</p>
                              <p className="text-sm text-yellow-700">{report.hazards_warnings}</p>
                            </div>
                          )}
                        </div>

                        {/* Right Column - Marine Life & Notes */}
                        <div className="space-y-4">
                          {report.marine_life_observations && report.marine_life_observations.length > 0 && (
                            <div>
                              <h4 className="font-semibold text-gray-900 mb-2">🐠 Marine Life Observed</h4>
                              <div className="flex flex-wrap gap-2">
                                {report.marine_life_observations.map((species, idx) => (
                                  <span
                                    key={idx}
                                    className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium"
                                  >
                                    {species}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}

                          {report.additional_notes && (
                            <div>
                              <h4 className="font-semibold text-gray-900 mb-2">📝 Additional Notes</h4>
                              <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded-lg">
                                {report.additional_notes}
                              </p>
                            </div>
                          )}

                          {report.best_time_to_dive && (
                            <div>
                              <h4 className="font-semibold text-gray-900 mb-2">⏰ Best Time to Dive</h4>
                              <p className="text-sm text-gray-700">{report.best_time_to_dive}</p>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Images */}
                      {report.image_urls && report.image_urls.length > 0 && (
                        <div className="mt-6 pt-6 border-t">
                          <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                            <ImageIcon className="w-5 h-5 text-cyan-600" />
                            Photos ({report.image_urls.length})
                          </h4>
                          <div className="flex gap-2 overflow-x-auto pb-2">
                            {report.image_urls.map((imgUrl, idx) => (
                              <img
                                key={idx}
                                src={`${BACKEND_URL}${imgUrl}`}
                                alt={`Report photo ${idx + 1}`}
                                className="h-32 w-32 rounded-lg object-cover cursor-pointer hover:opacity-80 hover:scale-105 transition-all border-2 border-gray-200 flex-shrink-0"
                                onClick={() => {
                                  setLightboxImages(report.image_urls.map(url => `${BACKEND_URL}${url}`));
                                  setLightboxIndex(idx);
                                  setLightboxOpen(true);
                                }}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                                title="Click to view full size"
                              />
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Image Lightbox */}
      {lightboxOpen && (
        <ImageLightbox
          images={lightboxImages}
          currentIndex={lightboxIndex}
          onClose={() => setLightboxOpen(false)}
          onNavigate={(newIndex) => setLightboxIndex(newIndex)}
        />
      )}
    </>
  );
};

export default DiveSiteReportsManagement;
